import { Maybe } from '@csp/csp-common-model';
import { useOnMount } from '@csp/fe-hooks';
import { StorageService } from '@csp/web-storage';

import { useState } from 'react';
import { v4 as uuid } from 'uuid';

const STORAGE_KEY_IS_RELOADING_BROWSER_PAGE = 'useBrowserTabId__isReloadingBrowserPage';
const STORAGE_KEY_TAB_ID = 'useBrowserTabId__tabId';

const resolveTabId = (): string => {
  const tabIdFromStorage = StorageService.sessionStorage.getItem(STORAGE_KEY_TAB_ID);

  // Session storage is copied when duplicating a browser tab. The below logic tries to figure
  // out if the tab was duplicated or not by checking what we have previously stored in session storage.
  const isReloadingBrowserPage =
    StorageService.sessionStorage.getItem(STORAGE_KEY_IS_RELOADING_BROWSER_PAGE) === 'true';
  const didDuplicateTab = tabIdFromStorage && !isReloadingBrowserPage;

  if (didDuplicateTab || !tabIdFromStorage) {
    const tabId = uuid();
    StorageService.sessionStorage.setItem(STORAGE_KEY_TAB_ID, tabId);
    return tabId;
  }

  return tabIdFromStorage;
};

// Returns a unique browser tab id that is persistent throughout the session.
// This hook is meant to be used only once and if used in multiple places in the
// app we would need to refactor how we store this state.
export const useBrowserTabId = (): Maybe<string> => {
  const [tabId, setTabId] = useState<Maybe<string>>(undefined);

  useOnMount(() => {
    const resolvedTabId = resolveTabId();
    setTabId(resolvedTabId);
    StorageService.sessionStorage.setItem(STORAGE_KEY_IS_RELOADING_BROWSER_PAGE, 'false');

    window.addEventListener('beforeunload', () => {
      StorageService.sessionStorage.setItem(STORAGE_KEY_IS_RELOADING_BROWSER_PAGE, 'true');
    });
  });

  return tabId;
};
